import React, { useState, useEffect } from "react";
import axios from "axios";
import { Warning, Lightning, X, ArrowRight, Crown } from "@phosphor-icons/react";
import { API, getAuthHeaders } from "@/lib/api";

/**
 * Low-credit banner. Shows at the top of the dashboard when:
 *  - user is on free tier AND credits <= 10 (out of default 50)
 *  - user is on a paid tier AND credits <= 20% of plan allocation
 * Dismissible (remembers per-session) so it's not annoying.
 */
export default function LowCreditBanner({ onUpgrade }) {
  const [show, setShow] = useState(false);
  const [credits, setCredits] = useState(0);
  const [tier, setTier] = useState("free");
  const [threshold, setThreshold] = useState(10);

  useEffect(() => {
    // Don't show if user dismissed this session
    if (sessionStorage.getItem("low_credit_banner_dismissed") === "1") return;
    (async () => {
      try {
        // Prefer the NEW plan engine — if the user is on a paid tier
        // (post-Phase-1 migration), suppress this legacy banner entirely.
        try {
          const planRes = await axios.get(`${API}/plans/me`);
          if (planRes.data?.is_admin_unlimited) return;
          if (planRes.data?.migrated_from_credits) return;  // migrated → new banner handles it
          if ((planRes.data?.tier || "free") !== "free") return;
        } catch (_) { /* fall through to legacy check */ }

        const { data } = await axios.get(`${API}/subscription/status`, {
          headers: getAuthHeaders(),
          withCredentials: true,
        });
        const c = data.credits ?? 0;
        const t = data.subscription_tier || "free";
        const isUnlimited = c === -1;
        setCredits(c);
        setTier(t);

        // Skip banner for admins / unlimited
        if (isUnlimited) return;

        // Threshold math
        let thr = 10;
        if (t === "creator") thr = 20;       // 20% of 100
        else if (t === "pro") thr = 60;      // 20% of 300
        else if (t === "business") thr = 140; // 20% of 700
        setThreshold(thr);

        if (c <= thr) setShow(true);
      } catch (_) {}
    })();
  }, []);

  const dismiss = () => {
    sessionStorage.setItem("low_credit_banner_dismissed", "1");
    setShow(false);
  };

  if (!show) return null;

  const isOutOfCredits = credits <= 0;
  const isFree = tier === "free";

  return (
    <div
      data-testid="low-credit-banner"
      className={`relative rounded-xl p-4 mb-4 border-2 flex items-start gap-3 ${
        isOutOfCredits
          ? "bg-gradient-to-r from-rose-50 via-pink-50 to-white border-rose-200"
          : "bg-gradient-to-r from-amber-50 via-yellow-50 to-white border-amber-200"
      }`}
    >
      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${isOutOfCredits ? "bg-rose-500/10" : "bg-amber-400/15"}`}>
        {isOutOfCredits ? (
          <Warning size={20} weight="fill" className="text-rose-500" />
        ) : (
          <Lightning size={20} weight="fill" className="text-amber-500" />
        )}
      </div>
      <div className="flex-1">
        <h4 className="text-sm font-bold text-zinc-900">
          {isOutOfCredits
            ? "You're out of platform credits"
            : `${credits} credit${credits === 1 ? "" : "s"} left — running low`}
        </h4>
        <p className="text-xs text-zinc-600 mt-0.5 leading-relaxed">
          {isFree ? (
            <>
              You've been using the free trial. Pick a plan to keep creating without limits.
              Creator starts at $19.99/mo (100 credits).
            </>
          ) : (
            <>
              Your {tier} plan credits will reset on your next billing cycle. Upgrade or top up to keep going this month.
            </>
          )}
        </p>
        <button
          onClick={onUpgrade}
          data-testid="low-credit-upgrade-btn"
          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E91E8C] text-white text-xs font-semibold hover:bg-[#c91876]"
        >
          <Crown size={12} weight="fill" />
          {isFree ? "Pick a plan" : "Upgrade"}
          <ArrowRight size={12} />
        </button>
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="flex-shrink-0 text-zinc-400 hover:text-zinc-700 p-1"
        data-testid="low-credit-dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
}
